const normalizeText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).filter(Boolean).join(', ');
  }
  return '';
};

/** Normalize create_order / axios error payloads into a user-facing message. */
export const extractApiErrorMessage = (
  response,
  fallback = 'Failed to place order.',
) => {
  if (!response) return fallback;

  const payload = response?.data ?? response;
  if (typeof payload === 'string') {
    const text = normalizeText(payload);
    return text || fallback;
  }

  const candidates = [
    payload?.message,
    payload?.error,
    payload?.errorMessage,
    payload?.msg,
    response?.message,
    response?.error,
    Array.isArray(payload?.errors) ? payload.errors : null,
  ];

  for (const candidate of candidates) {
    const text = normalizeText(candidate);
    if (text) return text;
  }

  return fallback;
};

export const resolveApiResponseStatus = (response) => {
  const top = Number(response?.status);
  const nested = Number(response?.data?.status);
  if (Number.isFinite(top)) return top;
  if (Number.isFinite(nested)) return nested;
  return null;
};

export const isApiSuccessResponse = (response) =>
  resolveApiResponseStatus(response) === 200;

const RECOVERABLE_TIMEOUT_STATUSES = new Set([408, 502, 503, 504, 524]);

/**
 * Gateway/proxy dropped create_order (screenshot: 504 at ~30s) while BitGo
 * place-order is still running. The order often exists a few seconds later.
 */
export const isRecoverablePlaceOrderTimeout = (errorOrResponse) => {
  if (!errorOrResponse) return false;
  const status = resolveApiResponseStatus(errorOrResponse);
  if (status != null && RECOVERABLE_TIMEOUT_STATUSES.has(status)) return true;
  const code = String(errorOrResponse?.code || '');
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true;
  const message = normalizeText(
    errorOrResponse?.message ||
      errorOrResponse?.data?.message ||
      errorOrResponse?.data,
  );
  if (/^network error$/i.test(message)) return true;
  if (/gateway time-?out/i.test(message)) return true;
  if (/timeout of \d+ms exceeded/i.test(message)) return true;
  return false;
};

const scaledClose = (left, right) => {
  const a = Number(left);
  const b = Number(right);
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return String(left ?? '') === String(right ?? '');
  }
  const epsilon = Math.max(1e-8, Math.abs(b) * 1e-8);
  if (Math.abs(a - b) <= epsilon) return true;
  const smallest = 1e8;
  if (Math.abs(a - b * smallest) <= 1) return true;
  if (Math.abs(a * smallest - b) <= 1) return true;
  return false;
};

const rowQuantity = (row) =>
  row?.quantity ?? row?.qty ?? row?.amount ?? row?.filled_amount;

export const rowMatchesSubmittedOrder = (row, submitted) => {
  if (!row || !submitted) return false;
  if (
    submitted.side != null &&
    row.side != null &&
    Number(row.side) !== Number(submitted.side)
  ) {
    return false;
  }
  if (submitted.quantity != null && rowQuantity(row) != null) {
    if (!scaledClose(rowQuantity(row), submitted.quantity)) return false;
  }
  if (submitted.price != null && row.price != null) {
    if (!scaledClose(row.price, submitted.price)) return false;
  }
  return true;
};

export const PLACE_ORDER_TIMEOUT_MESSAGE =
  'The order is still being processed. Check Orders before confirming again.';
